import { forwardRef } from 'react';

/**
 * This component let user to read / write value programatically in the uncontrolled input component
 * when used with useForm hook, so that developers can write complex input components while keeping the interface
 * as simple as <ComplexInput {...register('name')} />
 */
const ProxyInput = forwardRef(({ value, onValueChange }, ref) => {
  return (
    <input
      type='hidden'
      ref={(el) => {
        if (el !== null && ref !== null && typeof ref === 'function') {
          ref(
            new Proxy(el, {
              set(target, prop, newValue) {
                if (prop === 'value') {
                  onValueChange?.(newValue);
                  value.current = newValue;
                }

                target[prop as 'value'] = newValue; // Just to keep TSC happy

                return true;
              },
              get(target, prop) {
                if (prop === 'value') {
                  return value.current;
                }

                if (typeof target[prop as 'querySelectorAll'] === 'function') {
                  return target[prop as 'querySelectorAll'].bind(target); // Just to keep TSC happy
                }

                return target[prop as keyof typeof target]; // Just to keep TSC happy
              },
            }),
          );
        }
      }}
    />
  );
}) as <ValueType>(props: {
  value: React.MutableRefObject<ValueType | undefined>;
  onValueChange?: (value: string) => void;
  ref: React.RefCallback<HTMLInputElement>;
}) => React.ReactElement;

export { ProxyInput };
